import { queryKeys } from '@qse/api'
import { AppRoutesList, type User } from '@qse/constants'
import { detectCurrentApp, getDefaultRoute, storage } from '@qse/utils'
import { useQueryClient } from '@tanstack/react-query'
import { FormikHelpers } from 'formik'
import { useNavigate } from 'react-router-dom'
import * as Yup from 'yup'

import { authService } from './auth-service'

interface TOTPFormData {
  code: string
}

export const use2FA = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const totpValidationSchema = Yup.object({
    code: Yup.string()
      .required('Verification code is required')
      .matches(/^\d{6}$/, 'Please enter a valid 6-digit code')
      .length(6, 'Code must be exactly 6 digits'),
  })

  // Handle TOTP verification
  const handleTOTP = async (
    values: TOTPFormData,
    {
      setSubmitting,
      setStatus,
      onSuccess,
    }: Partial<FormikHelpers<TOTPFormData>> & {
      onSuccess?: (response: User) => void
    },
  ) => {
    try {
      // Verify the TOTP code
      await authService.verifyTOTP(values.code)

      // Complete the 2FA setup and get user session
      const user = await authService.complete2FASetup()

      // Refresh user data from API to ensure we have the latest information
      try {
        await queryClient.invalidateQueries({ queryKey: queryKeys.auth.me() })
        await queryClient.refetchQueries({ queryKey: queryKeys.auth.me() })
      } catch (error) {
        console.error('Error refreshing user data after 2FA setup:', error)
        // Continue with login even if API refresh fails
      }

      // Call onSuccess callback if provided
      onSuccess?.(user)

      // Role-based navigation using centralized logic
      const { appType } = detectCurrentApp()
      const defaultRoute = getDefaultRoute(user.userRole, appType)

      navigate(defaultRoute, { replace: true })
    } catch (error: any) {
      authService.handleError(error, 'mfaVerification', setStatus)
    } finally {
      setSubmitting?.(false)
    }
  }

  // Handle disable TOTP setup (for non-first-time users)
  const handleDisableTOTP = async (isFirstLogin: boolean = false, user?: any) => {
    try {
      if (isFirstLogin) {
        // First-time users should be encouraged to set up 2FA
        // But we'll allow them to disable with a more prominent warning
        await authService.disable2FASetup()
      } else {
        // Regular users can disable more easily
        await authService.disable2FASetup()
      }

      // Set MFA verification flag since user skipped 2FA (no verification needed)
      storage.localStorage.setItem('isMfaVerified', 'true')

      // Set MFA step completed flag since user went through the setup screen
      storage.localStorage.setItem('mfaStepCompleted', 'true')

      // Role-based navigation using centralized logic
      if (user && user.userRole) {
        const { appType } = detectCurrentApp()
        const defaultRoute = getDefaultRoute(user.userRole, appType)

        navigate(defaultRoute, { replace: true })
      } else {
        // Fallback
        navigate(AppRoutesList.dashboard, { replace: true })
      }
    } catch (error) {
      console.error('Error skipping TOTP setup:', error)
    }
  }

  return {
    handleTOTP,
    handleDisableTOTP,
    totpValidationSchema,
  }
}
